import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {format,isValid,parseISO} from 'date-fns';
import {sl} from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArchive,FiRotateCcw,FiTrash2,FiUser,FiEuro,FiClock,FiCalendar,FiPackage}=FiIcons;

const ArchivedInvoices=({archivedInvoices,onRestoreInvoice,onPermanentDelete})=> {
  const [confirmDelete,setConfirmDelete]=useState(null);

  const handleRestore=(invoiceId,clientName)=> {
    if (window.confirm(`Ali želite obnoviti račun za ${clientName}? Račun bo ponovno aktiven.`)) {
      onRestoreInvoice(invoiceId);
    }
  };

  const handlePermanentDelete=(invoiceId,clientName)=> {
    if (confirmDelete===invoiceId) {
      onPermanentDelete(invoiceId);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(invoiceId);
      setTimeout(()=> setConfirmDelete(null),5000); // Reset after 5 seconds
    }
  };

  const getTotalAmount=(invoice)=> {
    return invoice.totalAmount || invoice.total_amount || invoice.amount || 0;
  };

  const getIntervalFull=(interval)=> {
    switch (interval) {
      case 'monthly': return 'Mesečno';
      case 'quarterly': return 'Četrtletno';
      case 'half-yearly': return 'Polletno';
      case 'yearly': return 'Letno';
      default: return 'Mesečno';
    }
  };

  // Safe date formatting function
  const formatSafeDate=(dateValue)=> {
    try {
      if (!dateValue) return 'Ni datuma';
      
      let date;
      if (typeof dateValue === 'string') {
        date = parseISO(dateValue);
      } else {
        date = new Date(dateValue);
      }
      
      if (!isValid(date)) {
        console.warn('Invalid date for archive:', dateValue);
        return 'Neveljaven datum';
      }
      
      return format(date, 'd.M.yyyy', { locale: sl });
    } catch (error) {
      console.error('Date formatting error:', error, 'for value:', dateValue);
      return 'Napaka datuma';
    }
  };

  // Sort archived invoices: first by archived date (newest first), then alphabetically
  const sortedInvoices = [...archivedInvoices].sort((a, b) => {
    try {
      // Handle archived_at date
      const dateA = new Date(a.archived_at || a.archivedAt || Date.now());
      const dateB = new Date(b.archived_at || b.archivedAt || Date.now());
      
      // First sort by archived date (newest first)
      if (isValid(dateA) && isValid(dateB) && dateA.getTime() !== dateB.getTime()) {
        return dateB - dateA;
      }
      
      // If dates are the same, sort alphabetically by customer name
      const nameA = (a.stranka || a.clientName || '').toLowerCase();
      const nameB = (b.stranka || b.clientName || '').toLowerCase();
      return nameA.localeCompare(nameB, 'sl');
    } catch (error) {
      console.error('Sorting error:', error);
      return 0;
    }
  });

  if (archivedInvoices.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-16"
      >
        <SafeIcon icon={FiArchive} className="text-6xl text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[#4d504a] mb-2">Arhiv je prazen</h3>
        <p className="text-[#6e725c]">Tukaj bodo prikazani arhivirani računi.</p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#4d504a] mb-2">Arhivirani računi</h2>
        <p className="text-[#6e725c]">
          {archivedInvoices.length} arhiviran{archivedInvoices.length === 1 ? '' : archivedInvoices.length === 2 ? 'a' : archivedInvoices.length <= 4 ? 'i' : 'ih'} račun{archivedInvoices.length === 1 ? '' : archivedInvoices.length === 2 ? 'a' : archivedInvoices.length <= 4 ? 'i' : 'ov'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
        {sortedInvoices.map((invoice, index) => (
          <motion.div
            key={invoice.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-gray-50 rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow border-l-4 border-l-gray-400 h-fit"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center min-w-0 flex-1">
                <SafeIcon icon={FiUser} className="text-[#6e725c] mr-2 flex-shrink-0 text-sm" />
                <h3 className="font-semibold text-[#4d504a] text-sm truncate">
                  {invoice.stranka || invoice.clientName}
                </h3>
                {invoice.line_items && invoice.line_items.length > 0 && (
                  <div className="ml-1 relative flex-shrink-0">
                    <SafeIcon icon={FiPackage} className="text-[#6e725c] text-xs" />
                    <span className="absolute -top-1 -right-1 bg-gray-400 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center text-[9px]">
                      {invoice.line_items.length}
                    </span>
                  </div>
                )}
              </div>
              <span className="ml-2 px-2 py-1 bg-gray-200 text-[#6e725c] text-xs rounded-full">
                ARHIVIRAN
              </span>
            </div>

            {/* Amount */}
            <div className="flex items-center mb-3">
              <SafeIcon icon={FiEuro} className="text-[#6e725c] mr-2 text-sm" />
              <div className="min-w-0 flex-1">
                <span className="font-bold text-[#4d504a] text-lg block">
                  {getTotalAmount(invoice).toFixed(2)} €
                </span>
              </div>
            </div>

            {/* Info row */}
            <div className="flex items-center justify-between mb-3 text-xs">
              <div className="flex items-center">
                <SafeIcon icon={FiClock} className="text-[#6e725c] mr-1" />
                <span className="text-[#4d504a]">{getIntervalFull(invoice.interval)}</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiArchive} className="text-[#6e725c] mr-1" />
                <span className="text-[#4d504a]">
                  {formatSafeDate(invoice.archived_at || invoice.archivedAt)}
                </span>
              </div>
            </div>

            {/* Line items info */}
            {invoice.line_items && invoice.line_items.length > 0 && (
              <div className="mb-3">
                <span className="text-xs text-[#6e725c]">
                  +{invoice.line_items.length} postavk{invoice.line_items.length === 1 ? 'a' : invoice.line_items.length <= 4 ? 'e' : ''}
                </span>
              </div>
            )}

            {/* Description */}
            {invoice.description && (
              <p className="text-[#4d504a] text-xs mb-3 line-clamp-2">{invoice.description}</p>
            )}

            {/* Action buttons */}
            <div className="flex space-x-2">
              <motion.button
                onClick={() => handleRestore(invoice.id, invoice.stranka || invoice.clientName)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 bg-[#80b238] text-white px-3 py-2 rounded text-xs font-medium hover:bg-[#6d9530] transition-colors flex items-center justify-center"
                title="Obnovi račun"
              >
                <SafeIcon icon={FiRotateCcw} className="mr-1 text-xs" />
                Obnovi
              </motion.button>

              <motion.button
                onClick={() => handlePermanentDelete(invoice.id, invoice.stranka || invoice.clientName)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`p-2 rounded transition-colors ${
                  confirmDelete === invoice.id
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-300 text-[#6e725c] hover:bg-red-400 hover:text-white'
                }`}
                title={confirmDelete === invoice.id ? 'Kliknite ponovno za potrditev' : 'Trajno izbriši'}
              >
                <SafeIcon icon={FiTrash2} className="text-xs" />
              </motion.button>
            </div>

            {/* Confirmation message */}
            {confirmDelete === invoice.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs"
              >
                <p className="text-red-700 font-medium">
                  ⚠️ Trajno brisanje! Kliknite ponovno za potrditev.
                </p>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ArchivedInvoices;